import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  type AppointmentBookingStatus,
  type AppointmentOutcome,
  BOOKING_STATUS_VALUES,
  getBookingStatusInfo,
  getOutcomeStatusInfo,
  OUTCOME_STATUS_VALUES,
} from "./appointment-status-style";

interface BookingStatusSelectProps {
  value: string;
  onValueChange: (value: string) => void;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
}

export function BookingStatusSelect({
  value,
  onValueChange,
  disabled = false,
  className = "h-9",
  placeholder = "Status",
}: BookingStatusSelectProps) {
  const selectedStatusInfo = value ? getBookingStatusInfo(value as AppointmentBookingStatus) : null;
  const SelectedIcon = selectedStatusInfo?.icon;

  return (
    <Select value={value} onValueChange={onValueChange} disabled={disabled}>
      <SelectTrigger className={className}>
        <SelectValue placeholder={placeholder}>
          {selectedStatusInfo && SelectedIcon && (
            <div className="flex items-center gap-2">
              <SelectedIcon className={`w-4 h-4 ${selectedStatusInfo.iconColor}`} />
              {selectedStatusInfo.text}
            </div>
          )}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {BOOKING_STATUS_VALUES.map((status) => {
          const statusInfo = getBookingStatusInfo(status);
          const IconComponent = statusInfo.icon;
          return (
            <SelectItem key={status} value={status}>
              <div className="flex items-center gap-2">
                <IconComponent className={`w-4 h-4 ${statusInfo.iconColor}`} />
                {statusInfo.text}
              </div>
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
}

interface OutcomeSelectProps {
  value: string | null;
  onValueChange: (value: string | null) => void;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
}

export function OutcomeSelect({
  value,
  onValueChange,
  disabled = false,
  className = "h-9",
  placeholder = "Outcome",
}: OutcomeSelectProps) {
  const handleChange = (newValue: string) => {
    onValueChange(newValue === "none" ? null : newValue);
  };

  const selectedOutcomeInfo = value ? getOutcomeStatusInfo(value as AppointmentOutcome) : null;
  const SelectedIcon = selectedOutcomeInfo?.icon;

  return (
    <Select value={value || "none"} onValueChange={handleChange} disabled={disabled}>
      <SelectTrigger className={className}>
        <SelectValue placeholder={placeholder}>
          {value ? (
            selectedOutcomeInfo && SelectedIcon ? (
              <div className="flex items-center gap-2">
                <SelectedIcon className={`w-3 h-3 ${selectedOutcomeInfo.iconColor}`} />
                {selectedOutcomeInfo.text}
              </div>
            ) : null
          ) : (
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-gray-400" />
              Not Started
            </div>
          )}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="none">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-gray-400" />
            Not Started
          </div>
        </SelectItem>
        {OUTCOME_STATUS_VALUES.filter(
          (outcome): outcome is NonNullable<typeof outcome> => outcome !== null
        ).map((outcome) => {
          const statusInfo = getOutcomeStatusInfo(outcome);
          const IconComponent = statusInfo.icon;
          return (
            <SelectItem key={outcome} value={outcome}>
              <div className="flex items-center gap-2">
                <IconComponent className={`w-3 h-3 ${statusInfo.iconColor}`} />
                {statusInfo.text}
              </div>
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
}
